import { useState } from 'react';
import { Card } from '../../../layout/Card.jsx';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { useAuth } from '../../../shared/useAuth.js';
import { formatIQD, formatDateTime } from '../../../shared/format.js';
import { useCashboxes, useCreateCashbox, useUpdateCashbox, useDeleteCashbox } from '../hooks/useCashboxes.js';
import { CashboxForm } from './CashboxForm.jsx';

const TYPE_LABELS = {
  reception: 'الاستقبال',
  cafeteria: 'الكافتيريا',
  toys: 'محل الألعاب',
  main: 'رئيسية',
  accounting: 'المحاسبة',
  other: 'أخرى'
};

const TYPE_TONES = {
  reception: 'info',
  cafeteria: 'orange',
  toys: 'warning',
  main: 'success',
  accounting: 'muted',
  other: 'muted'
};

function MovementsList({ movements = [] }) {
  if (!movements.length) {
    return <div className="text-ink-3 text-[11.5px]">لا توجد حركات حديثة</div>;
  }
  return (
    <ul className="flex flex-col gap-1 text-[11.5px]">
      {movements.slice(0, 3).map((m, i) => {
        const sign = Number(m.amount) >= 0 ? '+' : '';
        const tone = Number(m.amount) >= 0 ? 'text-green' : 'text-red';
        return (
          <li key={m._id || m.id || i} className="flex items-center gap-2">
            <span className="text-ink-3 truncate flex-1">
              {m.note || m.reason || m.type || 'حركة'}
            </span>
            <span className={`font-bold ${tone}`}>{sign}{formatIQD(m.amount || 0)}</span>
          </li>
        );
      })}
    </ul>
  );
}

function CashboxCard({ cb, canEdit, onEdit, onDelete }) {
  const balance = cb.balance ?? cb.currentBalance ?? 0;
  const movements = cb.recentMovements || cb.movements || [];

  return (
    <div className="card p-4 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-[14px] grid place-items-center bg-orange-soft"
          style={{ color: '#C2570D' }}
        >
          <Icon name="cashbox" size={24} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-bold text-[14.5px] truncate">{cb.name}</div>
            {cb.isMain ? <Pill variant="success">رئيسية</Pill> : null}
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <Pill variant={TYPE_TONES[cb.type] || 'muted'}>
              {TYPE_LABELS[cb.type] || cb.type || '—'}
            </Pill>
            {cb.isActive === false ? <Pill variant="muted">معطّلة</Pill> : null}
          </div>
        </div>
      </div>

      <div>
        <div className="text-[11px] text-ink-3">الرصيد الحالي</div>
        <div className="text-[24px] font-extrabold tracking-tight" style={{ color: '#C2570D' }}>
          {formatIQD(balance)}
        </div>
        {cb.lastMovementAt ? (
          <div className="text-[11px] text-ink-3 mt-0.5">
            آخر حركة: {formatDateTime(cb.lastMovementAt)}
          </div>
        ) : null}
      </div>

      <div className="border-t border-line pt-2.5">
        <div className="text-[11px] font-semibold text-ink-2 mb-1.5">آخر الحركات</div>
        <MovementsList movements={movements} />
      </div>

      <div className="flex items-center gap-1.5 pt-2 border-t border-line flex-wrap">
        {canEdit ? (
          <button
            type="button"
            className="btn"
            style={{ padding: '6px 10px' }}
            onClick={() => onEdit(cb)}
          >
            <Icon name="edit" size={13} /> تعديل
          </button>
        ) : null}
        {canEdit && !cb.isMain ? (
          <button
            type="button"
            className="btn btn-danger"
            style={{ padding: '6px 10px', marginInlineStart: 'auto' }}
            onClick={() => onDelete(cb)}
          >
            <Icon name="trash" size={13} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function CashboxList() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canEdit = hasPermission('settings.devices.manage');

  const { data: cashboxes = [], isLoading, error } = useCashboxes();
  const createCb = useCreateCashbox();
  const updateCb = useUpdateCashbox();
  const deleteCb = useDeleteCashbox();

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(cb) {
    setEditing(cb);
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  async function handleSubmit(body) {
    if (editing) {
      await updateCb.mutateAsync(
        { id: editing._id || editing.id, body },
        { onSuccess: () => closeForm() }
      );
    } else {
      await createCb.mutateAsync(body, { onSuccess: () => closeForm() });
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[15px] font-bold">القاصات</div>
          <div className="text-xs text-ink-3 mt-0.5">إجمالي {cashboxes.length} قاصة</div>
        </div>
        {canEdit ? (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" size={14} /> قاصة جديدة
          </button>
        ) : null}
      </div>

      {error ? (
        <Card>
          <div className="text-red text-center py-4">تعذّر تحميل القاصات</div>
        </Card>
      ) : isLoading ? (
        <Card>
          <div className="text-ink-3 text-center py-8">جاري التحميل...</div>
        </Card>
      ) : cashboxes.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-10 flex flex-col items-center gap-3">
            <Icon name="cashbox" size={42} color="#D6CCB3" />
            <div className="text-[14px] font-semibold text-ink-2">
              لا توجد قاصات — أنشئ أول قاصة
            </div>
            {canEdit ? (
              <button type="button" className="btn btn-primary" onClick={openCreate}>
                <Icon name="plus" size={14} /> قاصة جديدة
              </button>
            ) : null}
          </div>
        </Card>
      ) : (
        <div
          className="grid gap-3.5"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
        >
          {cashboxes.map((cb) => (
            <CashboxCard
              key={cb._id || cb.id}
              cb={cb}
              canEdit={canEdit}
              onEdit={openEdit}
              onDelete={(c) => setConfirmDelete(c)}
            />
          ))}
        </div>
      )}

      <CashboxForm
        open={formOpen}
        cashbox={editing}
        onClose={closeForm}
        onSubmit={handleSubmit}
        submitting={createCb.isPending || updateCb.isPending}
      />

      {confirmDelete ? (
        <div
          className="fixed inset-0 z-50 grid place-items-center"
          style={{ background: 'rgba(0,0,0,.45)' }}
        >
          <div
            className="card p-5 w-[420px] space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[15px] font-bold">حذف القاصة</h3>
            <div className="text-[13px] text-ink-2">
              هل تريد حذف القاصة "{confirmDelete.name}"؟ سيتعذّر الحذف إذا كان عليها رصيد أو حركات.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn" onClick={() => setConfirmDelete(null)} disabled={deleteCb.isPending}>
                إلغاء
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={deleteCb.isPending}
                onClick={async () => {
                  await deleteCb.mutateAsync(confirmDelete._id || confirmDelete.id, {
                    onSuccess: () => setConfirmDelete(null)
                  });
                }}
              >
                {deleteCb.isPending ? 'جاري...' : 'حذف'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
